import React, { useState } from 'react';
import { Hash, X } from 'lucide-react';

interface KeypadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (number: string) => void;
}

const KeypadModal: React.FC<KeypadModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [input, setInput] = useState('');

  const handleNumberClick = (num: string) => {
    if (input.length < 3) {
      const newInput = input + num;
      const numValue = parseInt(newInput, 10);
      if (numValue > 0 && numValue <= 999) {
        setInput(newInput);
      }
    }
  };

  const handleDelete = () => {
    setInput(prev => prev.slice(0, -1));
  };

  const handleSubmit = () => {
    if (input.length > 0) {
      onSubmit(input.padStart(3, '0'));
      setInput('');
      onClose();
    }
  };

  const handleCancel = () => {
    setInput('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <Hash className="h-5 w-5 mr-2 text-blue-600" />
            Enter Jersey Number
          </h2>
          <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mb-4">
          <div className="bg-gray-100 p-3 rounded-lg text-center">
            <span className="text-3xl font-bold tracking-wider font-mono">
              {input.padStart(3, '0')}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
            <button
              key={num}
              onClick={() => handleNumberClick(num.toString())}
              className="bg-gray-100 hover:bg-gray-200 rounded-lg p-4 text-xl font-semibold transition"
            >
              {num}
            </button>
          ))}
          <button
            onClick={handleDelete}
            className="bg-gray-200 hover:bg-gray-300 rounded-lg p-4 text-xl font-semibold col-span-1 transition"
          >
            ←
          </button>
          <button
            onClick={() => handleNumberClick('0')}
            className="bg-gray-100 hover:bg-gray-200 rounded-lg p-4 text-xl font-semibold transition"
          >
            0
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={handleCancel}
            className="bg-gray-100 hover:bg-gray-200 rounded-lg py-3 font-semibold transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={input.length === 0}
            className={`rounded-lg py-3 font-semibold transition ${
              input.length > 0
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-200 text-white cursor-not-allowed'
            }`}
          >
            Go
          </button>
        </div>
      </div>
    </div>
  );
};

export default KeypadModal;